"use client";

import { usePostHog } from "@posthog/react";
import { sendGAEvent } from "@next/third-parties/google";
import { trackMetaBrowserEvent } from "@/lib/meta-pixel";

type TikTokQueue = {
	track: (event: string, properties?: Record<string, string | number>) => void;
};

function getTikTokQueue(): TikTokQueue | null {
	if (typeof window === "undefined") return null;
	const ttq = (window as Window & { ttq?: TikTokQueue }).ttq;
	if (!ttq || typeof ttq.track !== "function") return null;
	return ttq;
}

const TIKTOK_CONTENT_ID = "lovepage_create_page";
const TIKTOK_CONTENT_TYPE = "product";

export function useAnalytics() {
	const posthog = usePostHog();

	return {
		trackCtaClick: (location: string) => {
			posthog?.capture("cta_click", { location });
			sendGAEvent("event", "cta_click", { location });
			trackMetaBrowserEvent("InitiateCheckout", {
				content_name: "create_page",
				location,
			});
			getTikTokQueue()?.track("InitiateCheckout", {
				content_id: TIKTOK_CONTENT_ID,
				content_type: TIKTOK_CONTENT_TYPE,
				content_name: "create_page",
				location,
			});
		},
		trackFormStep: (step: number, stepName: string) => {
			posthog?.capture("form_step_completed", { step, step_name: stepName });
			sendGAEvent("event", "form_step", { step, step_name: stepName });
			trackMetaBrowserEvent("ViewContent", {
				content_name: "create_page_form_step",
				step,
				step_name: stepName,
			});
			getTikTokQueue()?.track("FormStepCompleted", {
				step,
				step_name: stepName,
				form_name: "create_page",
			});
			getTikTokQueue()?.track("ViewContent", {
				content_id: `${TIKTOK_CONTENT_ID}_step_${step}`,
				content_type: TIKTOK_CONTENT_TYPE,
				content_name: "create_page_form_step",
				step,
				step_name: stepName,
			});
		},
		trackCheckoutStarted: (value: number, photosCount: number) => {
			posthog?.capture("checkout_started", {
				value,
				photos_count: photosCount,
			});
			sendGAEvent("event", "begin_checkout", { value, currency: "BRL" });
			trackMetaBrowserEvent("AddToCart", {
				value,
				currency: "BRL",
				content_name: "create_page_checkout",
				num_items: photosCount,
			});
			getTikTokQueue()?.track("AddToCart", {
				content_id: TIKTOK_CONTENT_ID,
				content_type: TIKTOK_CONTENT_TYPE,
				value,
				currency: "BRL",
				content_name: "create_page_checkout",
				num_items: photosCount,
			});
		},
		trackPaymentInfoAdded: (value: number, photosCount: number) => {
			posthog?.capture("payment_info_added", {
				value,
				photos_count: photosCount,
			});
			sendGAEvent("event", "add_payment_info", {
				value,
				currency: "BRL",
			});
			trackMetaBrowserEvent("AddPaymentInfo", {
				value,
				currency: "BRL",
				content_name: "create_page_checkout",
				num_items: photosCount,
			});
			getTikTokQueue()?.track("AddPaymentInfo", {
				content_id: TIKTOK_CONTENT_ID,
				content_type: TIKTOK_CONTENT_TYPE,
				value,
				currency: "BRL",
				content_name: "create_page_checkout",
				num_items: photosCount,
			});
		},
		trackPurchase: (value: number) => {
			posthog?.capture("purchase_completed", { value });
			sendGAEvent("event", "purchase", { value, currency: "BRL" });
			trackMetaBrowserEvent("Purchase", { value, currency: "BRL" });
			getTikTokQueue()?.track("Purchase", {
				content_id: TIKTOK_CONTENT_ID,
				content_type: TIKTOK_CONTENT_TYPE,
				value,
				currency: "BRL",
			});
		},
	};
}
